import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogClose, MatDialogContent, MatDialogActions } from "@angular/material/dialog";
import {Player, UpdatePlayerLineUpDialog} from "../../model/players";
import {Position} from "../../model/position";
import {LineUpService} from "../../services/lineUp.service";
import {GameService} from "../../services/game.service";
import {PlayerPositions} from "../../model/playerPositions";
import {captureMessageWithTags} from "../../../sentry/sentry";
import {MessageDialogService} from "../../services/message-dialog.service";
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';
import { TranslocoModule } from '@ngneat/transloco';

@Component({
    selector: 'app-dialog-update-player-lineup',
    templateUrl: './dialog-update-player-lineup.component.html',
    styleUrls: ['./dialog-update-player-lineup.component.scss'],
    imports: [MatDialogTitle, MatIconButton, MatDialogClose, MatIcon, CdkScrollable, MatDialogContent, FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatCheckbox, MatDialogActions, MatButton, TranslocoModule]
})
export class DialogUpdatePlayerLineupComponent implements OnInit {
  private fb = inject(FormBuilder);
  data = inject<UpdatePlayerLineUpDialog>(MAT_DIALOG_DATA);
  lineUp = inject(LineUpService);
  game = inject(GameService);
  updatePlayerLineupComponentMatDialogRef = inject<MatDialogRef<DialogUpdatePlayerLineupComponent>>(MatDialogRef);
  private cdr = inject(ChangeDetectorRef);

  messageDialog = inject(MessageDialogService)
  dialogTitle = '';
  isEdit = false;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  playerForm: FormGroup;
  playerId = '';
  fullName = '';
  jerseyNumber = 0;
  playerPositions: PlayerPositions[] = [];
  footballPositions: string[] = [];
  teamId = '';
  seasonId = '';

  ngOnInit() {
    this.initForm(this.data);
  }

  initForm(player: UpdatePlayerLineUpDialog) {
    this.isEdit = player.isEdit;
    this.playerId = player.playerId;
    this.fullName = player.fullName;
    this.jerseyNumber = player.jerseyNumber;
    this.playerPositions = player.playerPositions;
    this.footballPositions = Object.values(Position);
    this.teamId = player.teamId;
    this.seasonId = player.seasonId;
    if (player.isEdit) {
      this.dialogTitle = 'Modification d\'un joueur';
    } else {
      this.dialogTitle = 'Ajout d\'un joueur';
    }

    this.playerForm = this.fb.group(
      this.footballPositions.reduce((controls, position) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        controls[position] = new FormControl(this.checkPlayerPosition(player, position));
        return controls;
      }, {
      fullName: new FormControl(this.fullName),
      jerseyNumber: new FormControl(player.gamesPlayed ? player.gamesPlayed[0] ? player.gamesPlayed[0].gameJerseyNumber : this.jerseyNumber : this.jerseyNumber),
      playerPositions: new FormArray(this.playerPositions.map((pos: { position: string; }) => new FormControl(pos.position)))
    }));
  }

  checkPlayerPosition(player: Player, position: Position): boolean {
    return player.playerPositions.some(pos => pos.position === position);
  }

  validateJerseyNumber() {
    const jerseyNumber = this.playerForm.get('jerseyNumber');
    if (jerseyNumber?.value < 0) {
      jerseyNumber?.setValue(1);
    } else if (jerseyNumber?.value > 99) {
      jerseyNumber?.setValue(99);
    }
  }

  updatePlayer() {
    const gameId = this.game.game.id;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const playerJerseyNumber = this.playerForm.get('jerseyNumber').value as number;

    const player: Player = {
      id: this.isEdit ? this.playerId : '',
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      fullName: this.isEdit ? this.fullName : this.playerForm.get('fullName').value,
      jerseyNumber: Number(this.jerseyNumber),
      teamId: this.teamId,
      seasonId: this.seasonId ?? this.game.game.seasonId,
      gamesPlayed: [
        {
          gameJerseyNumber: playerJerseyNumber
        }
      ],
      playerPositions: this.getCheckedPositions()
    }

    if (this.isEdit) {
      this.lineUp.putEditPlayerToLineUp(gameId, player).subscribe({
        next: () => {
          this.updatePlayerLineupComponentMatDialogRef.close();
        },
        error: error => {
          if (error.status === 409) {
            this.messageDialog.openMessage(
              'validation.errorTitle',
              'validation.errorPlayerNotExist',
              'app.ok',
              'error',
            )
          } else {
            console.error('Error: ' + error);
            captureMessageWithTags("Impossible de modifier le joueur", {gameId, playerId: player.id});
            this.messageDialog.openMessage(
              'validation.errorTitle',
              'validation.errorUpdatePlayer',
              'app.ok',
              'error',
            )
          }
        }
      });
    } else {
      player.jerseyNumber = playerJerseyNumber;
      this.lineUp.postPlayerToLineUp(gameId, player).subscribe({
        next: () => {
          this.updatePlayerLineupComponentMatDialogRef.close();
        },
        error: error => {
          if (error.status === 409) {
            this.messageDialog.openMessage(
              'validation.errorTitle',
              'validation.errorExistingPlayer',
              'app.ok',
              'error',
            )
          } else {
            console.error('Error: ' + error);
            captureMessageWithTags(`Impossible d'ajouter le nouveau joueur.`, {gameId, playerId: player.id});
            this.messageDialog.openMessage(
              'validation.errorTitle',
              'validation.errorAddPlayer',
              'app.ok',
              'error',
            )
          }
        }
      });

    }
  }

  private getLineup() {
    return this.game.isTeamHome(this.teamId) ? this.lineUp.home : this.lineUp.away;
  }

  getCheckedPositions(): PlayerPositions[] {
    const checkedPositions: PlayerPositions[] = [];

    for (const position of this.footballPositions) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (this.playerForm.get(position).value) {
        checkedPositions.push({position : position});
      }
    }
    return checkedPositions;
  }

  atLeastOnePositionChecked(): boolean {
    for (const position of this.footballPositions) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (this.playerForm.get(position).value) {
        return true;
      }
    }
    return false;
  }
}
